import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CommaSeprator } from "../utility/utilityFunctions";
import KeyBenefitPopup from "./KeyBenefitPopup";
import API from "../../utils/API";
import { toast } from "react-toastify";
import { updateQuoteDetails } from "../../redux/slices/QuoteDetailsSlice";
import { useNavigate } from "react-router-dom";
import {
  IoDocumentTextOutline,
  IoListOutline,
  IoNewspaperOutline,
} from "react-icons/io5";

const QuotationCard = () => {
  const [showBenefitPopup, setShowBenefitPopup] = useState(false);
  const [selectedKeyBenifits, setSelectedKeyBenifits] = useState([]);
  const [buyLoadingId, setBuyLoadingId] = useState("");

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const quoteDetails = useSelector((state) => state.QuoteDetails);
  const enquiryState = useSelector((state) => state.Enquiry);

  const allQuotes = Array.isArray(quoteDetails?.quotes)
    ? quoteDetails.quotes
    : [];
  const sortPremium = quoteDetails?.filters?.sortPremium || "";
  const selectedInsurerIds =
    quoteDetails?.filters?.selectedInsurerIds instanceof Set
      ? quoteDetails.filters.selectedInsurerIds
      : new Set();
  const visibleQuotes =
    selectedInsurerIds.size === 0
      ? allQuotes
      : allQuotes.filter((row) =>
          selectedInsurerIds.has(
            Number(row?.InsurerID ?? row?.InsurerId ?? row?.insurerID ?? 0),
          ),
        );

  const getPremium = (row) =>
    Number(row?.NetPremium ?? row?.netPremium ?? row?.TotalPremium ?? 0);

  const getTopBenefits = (row) =>
    Array.isArray(row?.keyBenifits) ? row.keyBenifits.slice(0, 3) : [];

  const formatIndianCurrency = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return "₹0";
    return `$${new Intl.NumberFormat("en-IN").format(num)}`;
  };

  const sortedQuotes = (() => {
    if (!Array.isArray(visibleQuotes)) return [];
    if (sortPremium !== "high_to_low" && sortPremium !== "low_to_high") {
      return visibleQuotes;
    }
    const next = [...visibleQuotes];
    next.sort((a, b) => {
      const ap = getPremium(a);
      const bp = getPremium(b);
      return sortPremium === "high_to_low" ? bp - ap : ap - bp;
    });
    return next;
  })();

  const openKeyBenefitPopup = (keyBenifits) => {
    setSelectedKeyBenifits(keyBenifits);
    setShowBenefitPopup(true);
  };

  const closeKeyBenefitPopup = () => {
    setSelectedKeyBenifits([]);
    setShowBenefitPopup(false);
  };

  const getbrochure = (url) => {
    window.open(url, "_blank");
  };

  const getRowId = (row, index) =>
    String(
      row?.UniqueIdentifier ||
        row?.uniqueIdentifier ||
        row?.QuoteNo ||
        `${row?.InsurerID || row?.InsurerId || "ins"}-${row?.PlanId || row?.PlansID || row?.PlanName || index}`,
    );

  const saveQuotation = async (row, index) => {
    const id = getRowId(row, index);
    try {
      setBuyLoadingId(id);
      dispatch(updateQuoteDetails({ selectedQuotes: row }));

      const payload = {
        EnquiryNo: enquiryState?.EnquiryNo || "",
        NetPremium: Number(row?.NetPremium ?? row?.netPremium ?? 0),
        InsurerId: String(
          row?.InsurerID ?? row?.InsurerId ?? row?.insurerID ?? "",
        ),
        PlanName: row?.PlanName || "",
        SumAssured: String(row?.SumAssured ?? row?.sumAssured ?? 0),
        productName: row?.productName || row?.ProductName || "",
        Deductible: String(row?.Deductible ?? row?.deductible ?? ""),
        TransKey: row?.TransKey || row?.transKey || "",
        lotNo: enquiryState?.LotNo || "",
        TotalPremium: Number(row?.NetPremium ?? row?.netPremium ?? 0),
        PlanId: Number(row?.PlanId ?? row?.PlansID ?? row?.plansID ?? 0),
        GST: Number(row?.GST ?? row?.gst ?? 0),
        ProductCode: row?.ProductCode || row?.productCode || "",
        coverageTypeId: String(
          row?.coverageTypeId ?? row?.CoverageTypeId ?? "",
        ),
        IsVisitingUSACanada: Boolean(
          row?.IsVisitingUSACanada ?? row?.isVisitingUSACanada ?? false,
        ),
        PolicyType: row?.PolicyType ?? row?.policyType ?? null,
        QuoteNo: row?.QuoteNo ?? row?.quoteNo ?? null,
        UniqueIdentifier: id,
      };

      const res = await API.CallRequestWithHeader({
        URL: "SaveQuotation",
        PostString: JSON.stringify(payload),
      });

      if (res?.StatusCode === 200) {
        navigate("/Proposal");
      } else {
        toast.error(res?.Message || "Failed to save quotation");
      }
    } catch (e) {
      toast.error("Failed to save quotation");
    } finally {
      setBuyLoadingId("");
    }
  };

  return (
    <>
      {showBenefitPopup && (
        <KeyBenefitPopup
          isOpen={showBenefitPopup}
          onClose={closeKeyBenefitPopup}
          keyBenifits={selectedKeyBenifits}
        />
      )}

      <div className="qc-wrap">
        {/* ── Skeleton loader ── */}
        {quoteDetails?.quoteLoading &&
          [1, 2, 3].map((n) => (
            <div className="qc-skeleton" key={n}>
              <div className="qc-skel-card">
                <div className="qc-skel-top">
                  <div className="qc-skel-logo" />
                  <div className="qc-skel-plan">
                    <div
                      className="qc-skel-block"
                      style={{ width: "62%", height: 14 }}
                    />
                    <div
                      className="qc-skel-block"
                      style={{ width: "48%", height: 10 }}
                    />
                  </div>
                  <div className="qc-skel-cover">
                    <div
                      className="qc-skel-block"
                      style={{ width: 62, height: 10, marginLeft: "auto" }}
                    />
                    <div
                      className="qc-skel-block"
                      style={{ width: 104, height: 18, marginLeft: "auto" }}
                    />
                  </div>
                </div>

                <div className="qc-skel-divider" />

                <div className="qc-skel-mid">
                  <div className="qc-skel-benefits">
                    {[1, 2, 3].map((c) => (
                      <div key={c} className="qc-skel-benefit">
                        <div
                          className="qc-skel-block"
                          style={{ width: 20, height: 20, borderRadius: 6 }}
                        />
                        <div className="qc-skel-benefit-lines">
                          <div
                            className="qc-skel-block"
                            style={{ width: "88%", height: 11 }}
                          />
                          <div
                            className="qc-skel-block"
                            style={{ width: "66%", height: 10 }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="qc-skel-buy" />
                </div>

                <div className="qc-skel-footer">
                  <div
                    className="qc-skel-block"
                    style={{ width: 92, height: 30, borderRadius: 8 }}
                  />
                  <div className="qc-skel-footer-right">
                    <div
                      className="qc-skel-block"
                      style={{ width: 106, height: 30, borderRadius: 8 }}
                    />
                    <div
                      className="qc-skel-block"
                      style={{ width: 92, height: 30, borderRadius: 8 }}
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}

        {/* ── Empty state ── */}
        {!quoteDetails?.quoteLoading &&
          Array.isArray(visibleQuotes) &&
          visibleQuotes.length === 0 && (
            <div className="qc-empty">
              <div className="qc-empty-icon">
                <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                  <circle
                    cx="18"
                    cy="18"
                    r="11"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  />
                  <path
                    d="M26 26l7 7"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <path
                    d="M14 18h8M18 14v8"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
              <p className="qc-empty-title">No plans found</p>
              <p className="qc-empty-sub">
                Try adjusting your filters to see more options.
              </p>
            </div>
          )}

        {/* ── Quote cards ── */}
        {Array.isArray(sortedQuotes) &&
          sortedQuotes.map((row, i) => {
            const rowId = getRowId(row, i);
            const isLoading = buyLoadingId === rowId;
            const topBenefits = getTopBenefits(row);

            return (
              <div key={i} className="qc-card">
                {/* Top accent bar */}
                <div className="qc-card-body">
                  {/* ── Header row: logo / plan info / cover + buy ── */}
                  <div className="qc-header-row">
                    {/* Logo */}
                    <div className="qc-logo-box">
                      {row.InsurerLogo ? (
                        <img src={row.InsurerLogo} alt={row.PlanName} />
                      ) : (
                        <span className="qc-logo-fallback">
                          {(row.PlanName || "").slice(0, 2).toUpperCase()}
                        </span>
                      )}
                    </div>

                    {/* Plan info */}
                    <div className="qc-plan-info">
                      <div className="qc-plan-name">{row.PlanName}</div>
                      <div className="qc-plan-product">
                        {row.ProductName || row.PlanName}
                      </div>
                    </div>

                    {/* Cover amount */}
                    <div className="qc-cover-buy">
                      <div className="qc-cover-block">
                        <span className="qc-cover-label">Sum insured</span>
                        <span className="qc-cover-amount">
                          <span className="qc-rupee-sm">$</span>
                          {CommaSeprator(Math.round(row.SumAssured))}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Divider */}
                  {/* <div className="qc-divider" /> */}

                  {/* ── Benefit chips + Buy button ── */}
                  <div className="qc-middle-row">
                    {topBenefits.length > 0 && (
                      <div className="qc-benefits-grid">
                        {topBenefits.map((benefit, idx) => (
                          <div
                            key={`${benefit.CoverageDisplayName}-${idx}`}
                            className="qc-benefit-chip"
                          >
                            <div style={{ display: "flex", gap: "5px" }}>
                              {/* <span className="qc-benefit-num">{idx + 1}</span> */}
                              <div className="qc-benefit-name">
                                {benefit.CoverageDisplayName}
                              </div>
                            </div>
                            <div className="qc-benefit-meta">
                              {benefit.Limit
                                ? `Limit: ${formatIndianCurrency(benefit.Limit)}`
                                : "Limit: NA"}
                              {benefit.Deductible
                                ? ` · Deductible: ${benefit.Deductible}`
                                : ""}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    <button
                      className="qc-buy-btn qc-buy-btn--mid"
                      type="button"
                      onClick={() => saveQuotation(row, i)}
                      disabled={isLoading}
                      aria-busy={isLoading}
                    >
                      {isLoading ? (
                        <span className="qc-dots" aria-label="Loading">
                          <span className="qc-dot" />
                          <span className="qc-dot" />
                          <span className="qc-dot" />
                        </span>
                      ) : (
                        <>
                          <span className="qc-buy-label">Buy Now</span>
                          <span className="qc-buy-price">
                            <span className="qc-buy-rupee">₹</span>
                            {CommaSeprator(Math.round(row.NetPremium))}
                          </span>
                        </>
                      )}
                    </button>
                  </div>

                  {/* ── Footer ── */}
                  <div className="qc-footer">
                    <button
                      type="button"
                      className="qc-footer-btn"
                      onClick={() => openKeyBenefitPopup(row.keyBenifits)}
                    >
                      <IoListOutline className="qc-footer-icon" />
                      Key benefits
                    </button>

                    <div className="qc-footer-right">
                      <button
                        type="button"
                        className="qc-footer-btn"
                        onClick={() => getbrochure(row.Wordings)}
                      >
                        <IoDocumentTextOutline className="qc-footer-icon" />
                        Policy wording
                      </button>
                      <button
                        type="button"
                        className="qc-footer-btn"
                        onClick={() => getbrochure(row.Brochure)}
                      >
                        <IoNewspaperOutline className="qc-footer-icon" />
                        Brochure
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
      </div>
    </>
  );
};

export default QuotationCard;
